/**
 * Preflight in the export and Run workflow dialogs: when they open (and when a choice changes) PRISM checks
 * that the media and destinations are available, and Start or Export stays disabled with the reasons.
 *
 * Archive tiers (0.29.0): when the only problems are originals in an archive (ORIGINAL_ARCHIVED), preflight
 * is `restorable` and the dialogs offer Restore and export (Restore and start) instead.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Copyright, Loader2 } from 'lucide-react';
import type { PreflightSeverity, RightsAck } from '@prism/contracts';
import { api } from '../api/client';
import { useMe } from '../api/hooks';
import { can } from '../lib/permissions';
import { EMPTY_RIGHTS_ACK, ackProblemsOf, canOverrideOfError, isBlockingProblem, mergeProblems, problemsByAsset, rightsAckField, rightsAckNeeds, rightsAckReady, rightsProblemsOfError, type RightsAckState } from './rightsAck';

/** 1.8.0: `severity` (contracts PreflightSeverity) on rights problems; absent: block. */
export interface PreflightProblem { code: string; message: string; assetId?: string; title?: string; nodeId?: string; locationId?: string; restoring?: boolean; severity?: PreflightSeverity }
export interface PreflightResult { ok: boolean; problems: PreflightProblem[]; restorable?: boolean; waitsForRecording?: Array<{ assetId: string; title: string }> }

/** null params: nothing to check (the dialog is not ready). */
export function usePreflight(params: Record<string, string | null | undefined> | null) {
  const clean = params ? Object.fromEntries(Object.entries(params).filter(([, v]) => v)) as Record<string, string> : null;
  return useQuery({
    queryKey: ['preflight', clean],
    queryFn: () => api<PreflightResult>(`/api/v1/preflight?${new URLSearchParams(clean!).toString()}`),
    enabled: Boolean(clean),
    staleTime: 0,
    retry: false,
  });
}

/** Whether a start is allowed: only once preflight answered without problems. */
export const preflightBlocks = (q: ReturnType<typeof usePreflight>) => q.isLoading || Boolean(q.data && !q.data.ok);

/** Every problem is an original in an archive: a start that restores them first is allowed. */
export const preflightRestorable = (q: ReturnType<typeof usePreflight>) => Boolean(!q.isLoading && q.data && !q.data.ok && q.data.restorable);

export function PreflightProblems({ q, what = 'This export' }: { q: ReturnType<typeof usePreflight>; what?: string }) {
  if (q.isLoading) return <div className="faint" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }} data-testid="preflight-checking"><Loader2 size={12} className="spin" /> Checking media and destination…</div>;
  if (!q.data || q.data.ok) return null;
  // 1.8.0: the rights problems a user can answer (override, confirm) are in the rights block (RightsCheckBlock).
  const blocking = q.data.problems.filter(isBlockingProblem);
  if (q.data.restorable) {
    return (
      <div className="banner warning" role="status" data-testid="preflight-archived">
        <AlertTriangle size={15} style={{ flex: 'none', marginTop: 2 }} />
        <div className="banner-body">
          <strong>{q.data.problems.length === 1 ? 'An original is archived' : 'Some originals are archived'}</strong>
          {q.data.problems.map((p, i) => <span key={i} data-code={p.code}>{p.message}</span>)}
          <span className="muted">{what} can restore {q.data.problems.length === 1 ? 'it' : 'them'} first and start when {q.data.problems.length === 1 ? 'it is' : 'they are'} back: the archive takes minutes to hours.</span>
        </div>
      </div>
    );
  }
  return (
    <div className="banner error" role="alert" data-testid="preflight-problems">
      <AlertTriangle size={15} style={{ flex: 'none', marginTop: 2 }} />
      <div className="banner-body">
        <strong>{what} cannot start</strong>
        {blocking.map((p, i) => <span key={i} data-code={p.code}>{p.message}</span>)}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- 1.8.0 rights: confirm and override */

/**
 * The rights answers of a start (ExportPanel, AssetExport, BatchActions, SendToEdit, ShareAsset, RunWorkflow): the
 * problems from preflight (or from the start's own 409 RIGHTS_CONFIRMATION_REQUIRED / RIGHTS_OVERRIDE_REQUIRED), the
 * user's answers, and the `rights` field the start sends. While rights are off there are no such problems and the
 * start sends nothing new.
 */
export function useRightsCheck(preflightProblems?: readonly PreflightProblem[]) {
  // The server's word (409 RIGHTS_OVERRIDE_REQUIRED details.canOverride) wins over the permission once it has answered.
  const [serverCanOverride, setServerCanOverride] = useState<boolean | null>(null);
  const canManage = serverCanOverride ?? can(useMe().data, 'rights.manage');
  const [ack, setAck] = useState<RightsAckState>(EMPTY_RIGHTS_ACK);
  const [fromStart, setFromStart] = useState<PreflightProblem[]>([]);
  const problems = mergeProblems(ackProblemsOf(preflightProblems), fromStart);
  return {
    problems, ack, setAck, canManage,
    /** The answers are complete (always true without rights problems). */
    ready: rightsAckReady(problems, ack, canManage),
    /** Spread into the start's JSON body. */
    field: (): { rights?: RightsAck } => rightsAckField(problems, ack, canManage),
    /** A start's error: true when it asked for rights answers (the block now shows them), false for anything else. */
    takeError(err: unknown): boolean {
      const list = rightsProblemsOfError(err);
      if (!list) return false;
      setFromStart((prev) => mergeProblems(prev, ackProblemsOf(list)));
      const may = canOverrideOfError(err);
      if (may !== null) setServerCanOverride(may);
      return true;
    },
    reset() { setFromStart([]); setAck(EMPTY_RIGHTS_ACK); setServerCanOverride(null); },
  };
}
export type RightsCheck = ReturnType<typeof useRightsCheck>;

/**
 * The shared block: the rights problems per asset with their restriction text, "I've read the rights restrictions",
 * and for blocks a rights manager may override, the reason (rights.manage) or "Ask a rights manager".
 */
export function RightsCheckBlock({ check, compact = false }: { check: RightsCheck; compact?: boolean }) {
  if (!check.problems.length) return null;
  const needs = rightsAckNeeds(check.problems);
  const groups = problemsByAsset(check.problems);
  return (
    <div className={`banner warning rights-check${compact ? ' compact' : ''}`} role="status" data-testid="rights-problems">
      <Copyright size={15} style={{ flex: 'none', marginTop: 2 }} />
      <div className="banner-body">
        <strong>{needs.override ? 'Rights stop this unless a rights manager overrides' : 'Check the rights before you go ahead'}</strong>
        <ul className="rights-check-list">
          {groups.map((g) => (
            <li key={g.key} data-asset-id={g.problems[0]?.assetId}>
              {g.problems.map((p, i) => <span key={i} data-code={p.code} data-severity={p.severity}>{p.message}</span>)}
            </li>
          ))}
        </ul>
        <label className="toggle-row">
          <input type="checkbox" checked={check.ack.confirmed} onChange={(e) => check.setAck({ ...check.ack, confirmed: e.target.checked })} data-testid="rights-confirm" />
          <span>I’ve read the rights restrictions</span>
        </label>
        {needs.override && (check.canManage ? (
          <div className="field" style={{ margin: 0 }}>
            <label htmlFor="rights-override-reason">Override (rights managers): the reason</label>
            <input id="rights-override-reason" className="input" maxLength={500} value={check.ack.reason} placeholder="For example: cleared by phone with the agency"
              onChange={(e) => check.setAck({ ...check.ack, reason: e.target.value })} data-testid="rights-override-reason" />
          </div>
        ) : (
          <span className="muted" data-testid="rights-ask-manager">Ask a rights manager: only they can override this, with a reason.</span>
        ))}
      </div>
    </div>
  );
}
